import { MetastructureField, MetastructureFieldType } from '__types__';
import {
  resourceTypeBindingsUtils,
  type ResourceTypeWorkspaceBinding
} from 'v5/api/resource-types/resource-type-bindings.api';
import { mapAssignmentsToMetastructureFields } from './mapAssignmentsToMetastructureFields';

export type MergedWorkspaceMetastructure = {
  fields: MetastructureField[];
  /**
   * Always present when merge runs (even if empty).
   * Empty → all CF defs write global under this workspace URL.
   * Non-empty → only these defs write browsing-workspace overlays.
   */
  overlayCustomFieldDefinitionIds: ReadonlySet<number>;
};

/**
 * Append-only: workspace overlay fields that are active and not already on the
 * global metastructure are added after global fields.
 *
 * Returns clean fields plus a side-channel of overlay-only custom definition
 * ids for FormBuilder / applicant CF value writes.
 */
export function mergeWorkspaceMetastructureFields(
  globalFields: MetastructureField[],
  bindings: ResourceTypeWorkspaceBinding[],
  resourceTypeId: number,
  workspaceId: number
): MergedWorkspaceMetastructure {
  const binding = resourceTypeBindingsUtils.resolveBinding(
    bindings,
    resourceTypeId,
    workspaceId
  );
  const overlay = mapAssignmentsToMetastructureFields(
    binding.workspaceFields.filter(field => field.active)
  );

  if (overlay.length === 0) {
    return {
      fields: globalFields,
      overlayCustomFieldDefinitionIds: new Set()
    };
  }

  const existingCustomIds = new Set(
    globalFields
      .filter(
        (
          field
        ): field is Extract<
          MetastructureField,
          { type: MetastructureFieldType.Custom }
        > => field.type === MetastructureFieldType.Custom
      )
      .map(field => field.custom_field_definition_id)
  );
  const existingCoreNames = new Set(
    globalFields
      .filter(
        (
          field
        ): field is Extract<
          MetastructureField,
          { type: MetastructureFieldType.Core }
        > => field.type === MetastructureFieldType.Core
      )
      .map(field => field.name)
  );

  const appended = overlay.filter(field => {
    if (field.type === MetastructureFieldType.Custom) {
      return !existingCustomIds.has(field.custom_field_definition_id);
    }
    if (field.type === MetastructureFieldType.Core) {
      return !existingCoreNames.has(field.name);
    }
    return false;
  });

  const overlayCustomFieldDefinitionIds = new Set(
    appended
      .filter(
        (
          field
        ): field is Extract<
          MetastructureField,
          { type: MetastructureFieldType.Custom }
        > => field.type === MetastructureFieldType.Custom
      )
      .map(field => field.custom_field_definition_id)
  );

  return {
    fields: [...globalFields, ...appended],
    overlayCustomFieldDefinitionIds
  };
}

export function isResourceTypeEnabledInWorkspace(
  bindings: ResourceTypeWorkspaceBinding[],
  resourceTypeId: number,
  workspaceId: number
): boolean {
  return resourceTypeBindingsUtils.isEnabledInWorkspace(
    bindings,
    resourceTypeId,
    workspaceId
  );
}
